import React, { Dispatch, SetStateAction, useEffect, useRef } from "react";
import {
  Divider,
  Grid,
  Icon,
  IconButton,
  makeStyles,
  TextField,
  Theme,
  Typography
} from "@material-ui/core";
import { Check as CheckIcon } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { AsyncDropdown } from "src/apps/commons/dropdowns";
import {
  // listAllPartnerProducts,
  listAllPartners,
  listAllRemyndBrands,
  listAllRemyndCatLv1s,
  listAllRemyndCatLv2s,
  listAllRemyndCatLv3s,
  listAllRemyndProducts
} from "src/apps/commons/dropdowns/api";
import { toDropdownOrNull } from "src/utils";
import { CollapsiblePanel, SubHeader } from "src/apps/commons/dataDisplays";
import { EditHeaderState } from "./Edit";
import { IUserProductBrief } from "../types";

function toList<T>(data?: T | null): T[] | undefined {
  return data ? [data] : undefined;
}

const useStyles = makeStyles((theme: Theme) => ({
  subHeader: {
    fontSize: theme.typography.pxToRem(17),
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
    textAlign: "left"
  },
  marginTop: {
    marginTop: theme.spacing(2)
  },
  clearButton: {
    marginLeft: 5
  }
}));
type Props = {
  userProduct: IUserProductBrief;
  state: EditHeaderState;
  setState: Dispatch<SetStateAction<PartialNull<EditHeaderState>>>;
  error?: { message: string };
};

const RemyndProductPartner: React.FC<Props> = props => {
  const classes = useStyles();
  const { userProduct, state, setState, error } = props;

  const partnerProduct =
    state.remyndProductDropdown?.meta.partnerProduct ?? null;

  const { partner: oldPartner } = userProduct;

  const isSelectNewPartner =
    state.partnerDropdown && state.partnerDropdown.value !== oldPartner?.id;
  const isChangePartner =
    oldPartner && oldPartner.id !== state.partnerDropdown?.value;
  const newPartnerLabel = state.partnerDropdown?.label;
  const oldPartnerLabel = oldPartner?.label;

  const fieldRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) {
      fieldRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [error]);

  return (
    <CollapsiblePanel title="Step 1. Remynd Product / Partner" icon="edit">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SubHeader title="ข้อมูลที่ User กรอกมา" />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <TextField
            variant="outlined"
            fullWidth
            label="แบรนด์"
            value={userProduct.brand?.label ?? userProduct.customBrandName}
            placeholder="ไม่ระบุ"
            disabled
            InputProps={{
              readOnly: true
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            variant="outlined"
            fullWidth
            label="หมวดสินค้า Level 1"
            value={userProduct.categoryLv1?.label}
            placeholder="ไม่ระบุ"
            disabled
            InputProps={{
              readOnly: true
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            variant="outlined"
            fullWidth
            label="หมวดสินค้า Level 2"
            value={userProduct.categoryLv2?.label}
            placeholder="ไม่ระบุ"
            disabled
            InputProps={{
              readOnly: true
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            variant="outlined"
            fullWidth
            label="หมวดสินค้า Level 3"
            value={userProduct.categoryLv3?.label}
            placeholder="ไม่ระบุ"
            disabled
            InputProps={{
              readOnly: true
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Grid>

        {userProduct.requestWarranty && (
          <Grid item xs={12}>
            <div style={{ display: "inline-flex" }}>
              <CheckIcon fontSize="small" style={{ marginRight: 8 }} />
              <Typography variant="subtitle2" color="primary">
                ต้องการให้ช่วยลงทะเบียนการรับประกัน
              </Typography>
            </div>
          </Grid>
        )}

        <Grid item xs={12}>
          {/* <SubHeader title="Remynd Product" marginTop /> */}
          <div className={classes.marginTop}>
            <Typography className={classes.subHeader} gutterBottom>
              Remynd Product
              <IconButton
                aria-label="Clear Remynd Product fields"
                className={classes.clearButton}
                color="primary"
                size="small"
                onClick={() =>
                  setState(prevState => ({
                    ...prevState,
                    //clear all fields
                    remyndBrandDropdown: null,
                    remyndCategoryLv1Dropdown: null,
                    remyndCategoryLv2Dropdown: null,
                    remyndCategoryLv3Dropdown: null,
                    remyndProductDropdown: null
                  }))
                }
              >
                <Icon>cleaning_services</Icon>
              </IconButton>
            </Typography>
            <Divider light />
          </div>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <AsyncDropdown
            label={"แบรนด์"}
            fetchDropdown={listAllRemyndBrands}
            paginate={{
              page: 1,
              pageSize: 500,
              order: {
                optionKey: "NAME_TH",
                desc: false
              }
            }}
            disabled={!!state.remyndProductDropdown}
            value={state.remyndBrandDropdown}
            onChange={value =>
              setState(prevState => ({
                ...prevState,
                remyndBrandDropdown: value
              }))
            }
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <AsyncDropdown
            label={"หมวดสินค้า Level 1"}
            fetchDropdown={listAllRemyndCatLv1s}
            paginate={{
              page: 1,
              pageSize: 500,
              order: {
                optionKey: "NAME_TH",
                desc: false
              }
            }}
            value={state.remyndCategoryLv1Dropdown}
            disabled={!!state.remyndProductDropdown}
            onChange={value =>
              setState(prevState => ({
                ...prevState,
                remyndCategoryLv1Dropdown: value,
                // -- Must clear dependent fields too
                remyndCategoryLv2Dropdown: null,
                remyndCategoryLv3Dropdown: null,
                remyndProductDropdown: null
              }))
            }
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <AsyncDropdown
            label={
              state.remyndCategoryLv1Dropdown
                ? "หมวดสินค้า Level 2"
                : "กรุณาเลือก หมวดสินค้า Level 1"
            }
            disabled={
              !state.remyndCategoryLv1Dropdown || !!state.remyndProductDropdown
            }
            fetchDropdown={listAllRemyndCatLv2s}
            paginate={{
              page: 1,
              pageSize: 500,
              order: {
                optionKey: "NAME_TH",
                desc: false
              }
            }}
            additionalReqParams={{
              search: { lv1Id: state.remyndCategoryLv1Dropdown?.value }
            }}
            value={state.remyndCategoryLv2Dropdown}
            onChange={value =>
              setState(prevState => ({
                ...prevState,
                remyndCategoryLv2Dropdown: value,
                remyndCategoryLv3Dropdown: null,
                remyndProductDropdown: null
              }))
            }
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <AsyncDropdown
            label={
              state.remyndCategoryLv2Dropdown
                ? "หมวดสินค้า Level 3"
                : "กรุณาเลือก หมวดสินค้า Level 2"
            }
            disabled={
              !state.remyndCategoryLv2Dropdown ||
              !state.remyndCategoryLv1Dropdown ||
              !!state.remyndProductDropdown
            }
            fetchDropdown={listAllRemyndCatLv3s}
            paginate={{
              page: 1,
              pageSize: 500,
              order: {
                optionKey: "NAME_TH",
                desc: false
              }
            }}
            additionalReqParams={{
              search: { lv2Id: state.remyndCategoryLv2Dropdown?.value }
            }}
            value={state.remyndCategoryLv3Dropdown}
            onChange={value =>
              setState(prevState => ({
                ...prevState,
                remyndCategoryLv3Dropdown: value,
                remyndProductDropdown: null
              }))
            }
          />
        </Grid>

        <Grid item xs={12} ref={fieldRef}>
          <AsyncDropdown
            label={"สินค้า *"}
            disabled={false}
            fetchDropdown={listAllRemyndProducts}
            paginate={{
              page: 1,
              pageSize: 500,
              order: {
                optionKey: "NAME_TH",
                desc: false
              }
            }}
            additionalReqParams={{
              brandIds: toList(state?.remyndBrandDropdown?.value),
              categoryLv1Ids: toList(state?.remyndCategoryLv1Dropdown?.value),
              categoryLv2Ids: toList(state?.remyndCategoryLv2Dropdown?.value),
              categoryLv3Ids: toList(state?.remyndCategoryLv3Dropdown?.value)
            }}
            value={state.remyndProductDropdown}
            onChange={value => {
              const p = value?.meta;
              setState(prevState => ({
                ...prevState,
                remyndProductDropdown: value,
                // -- Auto select brand/cats
                remyndBrandDropdown:
                  p != null
                    ? toDropdownOrNull(p.brand)
                    : prevState.remyndBrandDropdown,
                remyndCategoryLv1Dropdown:
                  p != null
                    ? toDropdownOrNull(p.categoryLv1)
                    : prevState.remyndCategoryLv1Dropdown,
                remyndCategoryLv2Dropdown:
                  p != null
                    ? toDropdownOrNull(p.categoryLv2)
                    : prevState.remyndCategoryLv2Dropdown,
                remyndCategoryLv3Dropdown:
                  p != null
                    ? toDropdownOrNull(p.categoryLv3)
                    : prevState.remyndCategoryLv3Dropdown,
                // -- Auto select partner from product (if exists)
                partnerDropdown: toDropdownOrNull(p?.partner)
              }));
            }}
            error={Boolean(error)}
            helperText={error?.message}
          />
        </Grid>
        <Grid item xs={12}>
          <Alert severity="info">
            แบรนด์ / หมวดหมู่สินค้า / สินค้า ที่เลือกจะเป็นการ mapping product
            เพื่อหา master ข้อมูลการรับประกัน คู่มือ ไฟล์แนบ เท่านั้น
            ไม่ได้แก้ไขข้อมูลของ user
          </Alert>
        </Grid>

        <Grid item xs={12}>
          <SubHeader title="Partner" marginTop />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            variant="outlined"
            fullWidth
            label="แบรนด์"
            value={partnerProduct ? partnerProduct.brand?.label : ""}
            placeholder="ไม่ระบุ"
            disabled
            InputProps={{
              readOnly: true
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            variant="outlined"
            fullWidth
            label="หมวดสินค้า Level 1"
            value={partnerProduct ? partnerProduct.categoryLv1?.label : ""}
            placeholder="ไม่ระบุ"
            disabled
            InputProps={{
              readOnly: true
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            variant="outlined"
            fullWidth
            label="หมวดสินค้า Level 2"
            value={partnerProduct ? partnerProduct.categoryLv2?.label : ""}
            placeholder="ไม่ระบุ"
            disabled
            InputProps={{
              readOnly: true
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            variant="outlined"
            fullWidth
            label="หมวดสินค้า Level 3"
            value={partnerProduct ? partnerProduct.categoryLv3?.label : ""}
            placeholder="ไม่ระบุ"
            disabled
            InputProps={{
              readOnly: true
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          <AsyncDropdown
            label={"Partner"}
            fetchDropdown={listAllPartners}
            disabled={partnerProduct !== null}
            paginate={{
              page: 1,
              pageSize: 500,
              order: {
                optionKey: "NAME",
                desc: false
              }
            }}
            value={state.partnerDropdown}
            onChange={value =>
              setState(prevState => ({
                ...prevState,
                partnerDropdown: value
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <TextField
            variant="outlined"
            fullWidth
            label="Partner Product"
            value={partnerProduct ? partnerProduct.label : ""}
            placeholder="ไม่ระบุ"
            disabled
            InputProps={{
              readOnly: true
            }}
            InputLabelProps={{
              shrink: true
            }}
          />
          {/* <AsyncDropdown
            label={"Partner Product"}
            disabled
            fetchDropdown={listAllPartnerProducts}
            paginate={{
              page: 1,
              pageSize: 500,
              order: {
                optionKey: "NAME",
                desc: false
              }
            }}
            value={
              state.remyndProductDropdown?.meta.partnerProduct
                ? toDropdown(state.remyndProductDropdown?.meta.partnerProduct)
                : null
            }
            onChange={value}
          /> */}
        </Grid>
        {isSelectNewPartner && (
          <Grid item xs={12}>
            <Alert severity="success">
              [{newPartnerLabel}] จะเห็น User Product
              นี้ได้ทันทีหลังจากกดบันทึกหน้านี้
            </Alert>
          </Grid>
        )}
        {isChangePartner && (
          <Grid item xs={12}>
            <Alert severity="error">
              User Product นี้ได้ทำการ mapping partner ไปแล้ว
              หากแก้ไขจะทำให้ข้อมูลหายไปจากฝั่ง [{oldPartnerLabel}]
            </Alert>
          </Grid>
        )}
      </Grid>
    </CollapsiblePanel>
  );
};

export default RemyndProductPartner;
